// Bootstrap SPA: konfigurasi, sesi, penghala hash dan rangka (topbar + navigasi bawah).

import { html, mount, $ } from './core/html.js';
import { loadConfig, initAuth, session, onSession, hasRole, isStaff, get, post, signOut, changeLocale } from './core/api.js';
import { t, locales, getLocale } from './core/i18n.js';
import { icon, toast, showError, loading } from './core/ui.js';
import { installControls } from './core/controls.js';

const ROUTES = [
  ['/', () => import('./pages/home.js')],
  ['/search', () => import('./pages/search.js')],
  ['/property/:id', () => import('./pages/property.js')],
  ['/checkout/:id', () => import('./pages/checkout.js'), { auth: true }],
  ['/payment-return/:id', () => import('./pages/payment-return.js'), { auth: true }],
  ['/sandbox-pay/:ref', () => import('./pages/sandbox-pay.js'), { auth: true }],
  ['/login', () => import('./pages/login.js')],
  ['/bookings', () => import('./pages/bookings.js'), { auth: true }],
  ['/bookings/:id', () => import('./pages/booking-detail.js'), { auth: true }],
  ['/bookings/:id/receipt', () => import('./pages/receipt.js'), { auth: true }],
  ['/wishlist', () => import('./pages/wishlist.js'), { auth: true }],
  ['/messages', () => import('./pages/messages.js'), { auth: true }],
  ['/messages/:id', () => import('./pages/messages.js'), { auth: true }],
  ['/profile', () => import('./pages/profile.js'), { auth: true }],
  ['/notifications', () => import('./pages/notifications.js'), { auth: true }],
  ['/become-host', () => import('./pages/become-host.js'), { auth: true }],
  ['/support', () => import('./pages/support.js'), { auth: true }],
  ['/host', () => import('./pages/host/dashboard.js'), { host: true }],
  ['/host/properties', () => import('./pages/host/properties.js'), { host: true }],
  ['/host/properties/new', () => import('./pages/host/property-editor.js'), { host: true }],
  ['/host/properties/:id', () => import('./pages/host/property-editor.js'), { host: true }],
  ['/host/calendar', () => import('./pages/host/calendar.js'), { host: true }],
  ['/host/bookings', () => import('./pages/host/bookings.js'), { host: true }],
  ['/host/earnings', () => import('./pages/host/earnings.js'), { host: true }],
  ['/host/withdrawals', () => import('./pages/host/withdrawals.js'), { host: true }],
  ['/host/reviews', () => import('./pages/host/reviews.js'), { host: true }],
  ['/host/promotions', () => import('./pages/host/promotions.js'), { host: true }],
  ['/host/settings', () => import('./pages/host/settings.js'), { host: true }],
  ['/admin', () => import('./pages/admin/dashboard.js'), { staff: true }],
  ['/admin/:section', () => import('./pages/admin/sections.js'), { staff: true }],
  ['/admin/bookings/:id', () => import('./pages/admin/booking-trace.js'), { staff: true }],
  ['/admin/properties/:id', () => import('./pages/admin/property-review.js'), { staff: true }],
];

const compiled = ROUTES.map(([pattern, loader, guard = {}]) => {
  const keys = [];
  const re = new RegExp('^' + pattern.replace(/\/:([a-z]+)/g, (_, k) => { keys.push(k); return '/([^/]+)'; }) + '/?$');
  return { pattern, re, keys, loader, guard };
});

let cleanup = null;
let navSeq = 0;
let unread = { notifications: 0, messages: 0 };

export function parseHash() {
  const h = location.hash.replace(/^#/, '') || '/';
  const [path, qs = ''] = h.split('?');
  return { path, query: new URLSearchParams(qs) };
}

export function navigate(to, { replace = false } = {}) {
  if (replace) history.replaceState(null, '', '#' + to);
  else location.hash = to;
  if (replace) route();
}

async function route() {
  const { path, query } = parseHash();
  const main = $('#main');
  if (typeof cleanup === 'function') { try { cleanup(); } catch { /* abaikan */ } }
  cleanup = null;
  let match = null;
  // Laluan statik diutamakan berbanding laluan berparameter.
  const sorted = [...compiled].sort((a, b) => a.keys.length - b.keys.length);
  for (const r of sorted) {
    const m = r.re.exec(path);
    if (m) { match = { r, params: Object.fromEntries(r.keys.map((k, i) => [k, decodeURIComponent(m[i + 1])])) }; break; }
  }
  renderShell(path);
  if (!match) {
    navSeq++;
    mount(main, html`<div class="wrap empty"><h1>${t('page.not_found_title')}</h1><p>${t('page.not_found_text')}</p><a class="btn btn-primary" href="#/">${t('page.go_home')}</a></div>`);
    return;
  }
  const g = match.r.guard;
  if ((g.auth || g.host || g.staff) && !session.me) {
    navigate(`/login?next=${encodeURIComponent(path + (query.toString() ? '?' + query : ''))}`, { replace: true });
    return;
  }
  if (g.host && !hasRole('HOST')) { navigate('/become-host', { replace: true }); return; }
  if (g.staff && !isStaff()) {
    navSeq++;
    mount(main, html`<div class="wrap empty"><h1>${t('page.restricted_title')}</h1><p>${t('page.restricted_text')}</p></div>`);
    return;
  }
  // Setiap navigasi mendapat bekas paparan baharu: render lama yang selesai lewat menulis ke
  // elemen yang telah dibuang, jadi tidak boleh menimpa halaman semasa (elak race condition).
  const seq = ++navSeq;
  const view = document.createElement('div');
  main.replaceChildren(view);
  mount(view, loading());
  window.scrollTo(0, 0);
  try {
    const mod = await match.r.loader();
    const c = await mod.render(view, match.params, query);
    if (seq !== navSeq) { if (typeof c === 'function') c(); return; }
    cleanup = c;
    main.focus({ preventScroll: true });
  } catch (e) {
    if (seq !== navSeq) return;
    showError(e);
    mount(view, html`<div class="wrap empty"><h1>${t('page.load_failed')}</h1><p>${e.message}</p><button class="btn btn-primary" id="reload">${t('page.reload')}</button></div>`);
    view.querySelector('#reload').onclick = () => location.reload();
  }
}

// Pada skrin sempit, kod pendek (BM / EN) dipaparkan; nama penuh kekal dalam atribut label & title.
const SHORT_LANG = { ms: 'BM', en: 'EN' };
function langSwitcher(id, { compact = false } = {}) {
  return html`<label class="sr" for="${id}">${t('lang.label')}</label><select class="lang" id="${id}" data-lang>${locales.map((l) => html`<option value="${l.code}" lang="${l.code}" label="${compact ? SHORT_LANG[l.code] || l.code.toUpperCase() : l.name}" title="${l.name}" ${l.code === getLocale() ? 'selected' : ''}>${compact ? SHORT_LANG[l.code] || l.code.toUpperCase() : l.name}</option>`)}</select>`;
}

// Logo jenama: logo tersuai daripada tetapan platform (jika ditetapkan), atau logo rasmi Stay'D Dabong.
export function logoSrc(onDark = false) {
  const custom = session.config?.settings?.logo_url;
  if (!onDark && custom && /^https:\/\//.test(custom)) return custom;
  return onDark ? '/brand/logo-horizontal-white.png' : '/brand/logo-horizontal.png';
}

function renderFooter(cfg, name) {
  const el = $('#footer');
  if (!el) return;
  const email = cfg?.settings?.support_email;
  mount(el, html`<div class="wrap">
    <div class="cols">
      <div><a class="brand" href="#/" aria-label="${t('shell.home_aria', { name })}"><img class="brand-logo" src="/brand/logo-horizontal-white.png" alt="${name}" width="188" height="48" loading="lazy"></a>
        <p class="blurb">${t('shell.footer_blurb')}</p></div>
      <div><h4>${t('shell.footer_explore')}</h4><ul>
        <li><a href="#/search">${t('nav.explore')}</a></li>
        <li><a href="#/search?sort=newest">${t('home.newest')}</a></li>
        <li><a href="#/wishlist">${t('nav.wishlist')}</a></li></ul></div>
      <div><h4>${t('shell.footer_support')}</h4><ul>
        <li><a href="#/become-host">${t('nav.become_host')}</a></li>
        <li><a href="#/support">${t('nav.help')}</a></li>
        ${email ? html`<li><a href="mailto:${email}" translate="no">${email}</a></li>` : ''}</ul></div>
    </div>
    <div class="base"><span>© ${new Date().getFullYear()} <span translate="no">${name}</span>. ${t('shell.rights')}</span><span>${t('home.eyebrow')}</span></div>
  </div>`);
}

function renderShell(path) {
  const cfg = session.config;
  const me = session.me;
  const name = cfg?.settings?.platform_name || "Stay'D Dabong";
  const cur = (p) => (path === p || (p !== '/' && path.startsWith(p + '/')) ? 'page' : null);
  document.title = name;
  const skip = document.querySelector('.skip');
  if (skip) skip.textContent = t('a11y.skip');
  mount($('#topbar'), html`
    ${cfg?.settings?.sandbox_mode ? html`<div class="testbar">${t('shell.test_mode')}</div>` : ''}
    <div class="wrap">
      <a class="brand" href="#/" aria-label="${t('shell.home_aria', { name })}">
        <img class="brand-logo on-light" src="${logoSrc(false)}" alt="${name}" width="188" height="48" translate="no">
        <img class="brand-logo on-dark" src="${logoSrc(true)}" alt="" width="188" height="48" aria-hidden="true"></a>
      <nav class="topnav" aria-label="${t('shell.nav_top')}">
        <a href="#/search" aria-current="${cur('/search')}">${t('nav.explore')}</a>
        ${me ? html`<a href="#/bookings" aria-current="${cur('/bookings')}">${t('nav.bookings')}</a>
          <a href="#/wishlist" aria-current="${cur('/wishlist')}">${t('nav.wishlist')}</a>
          <a href="#/messages" aria-current="${cur('/messages')}">${t('nav.messages')}${unread.messages ? html`<span class="badge-dot">${unread.messages}</span>` : ''}</a>
          ${hasRole('HOST') ? html`<a href="#/host" aria-current="${cur('/host')}">${t('nav.host_area')}</a>` : html`<a href="#/become-host">${t('nav.become_host')}</a>`}
          ${isStaff() ? html`<a href="#/admin" aria-current="${cur('/admin')}">${t('nav.admin')}</a>` : ''}
          <a href="#/notifications" aria-label="${t('nav.notifications')}">${icon('bell')}${unread.notifications ? html`<span class="badge-dot">${unread.notifications}</span>` : ''}</a>
          <a href="#/profile" aria-current="${cur('/profile')}">${me.name.split(' ')[0]}</a>`
        : html`<a href="#/become-host">${t('nav.become_host')}</a><a class="btn btn-primary btn-sm" href="#/login">${t('nav.login')}</a>`}
        ${langSwitcher('lang-top')}
      </nav>
      <span class="acct">${langSwitcher('lang-mobile', { compact: window.innerWidth < 480 })}${!me ? html`<a class="btn btn-ghost btn-sm" href="#/login" style="display:inline-flex" data-mobile-login>${t('nav.login')}</a>` : ''}</span>
    </div>`);
  const acct = $('#topbar .acct');
  if (acct) acct.style.display = window.innerWidth >= 900 ? 'none' : '';
  const tab = (href, label, ic, badge) => html`<a href="#${href}" aria-current="${cur(href) || (href === '/' && path === '/') ? 'page' : null}">${icon(ic)}${label}${badge ? html`<span class="badge-dot">${badge}</span>` : ''}</a>`;
  renderFooter(cfg, name);
  $('#bottomnav').setAttribute('aria-label', t('shell.nav_main'));
  mount($('#bottomnav'), html`${tab('/search', t('nav.explore'), 'search')}${tab('/bookings', t('nav.bookings'), 'calendar')}${tab('/wishlist', t('nav.wishlist'), 'heart')}${tab('/messages', t('nav.messages'), 'chat', unread.messages)}${tab(me && hasRole('HOST') ? '/host' : '/profile', me && hasRole('HOST') ? t('nav.host_short') : t('nav.profile'), 'user', unread.notifications)}`);
}

async function pollUnread() {
  if (!session.me) return;
  try {
    const [n, c] = await Promise.all([get('/api/notifications'), get('/api/conversations')]);
    const next = { notifications: n.filter((x) => !x.read).length, messages: c.reduce((s, x) => s + x.unread_count, 0) };
    if (next.notifications !== unread.notifications || next.messages !== unread.messages) {
      unread = next;
      renderShell(parseHash().path);
    }
  } catch { /* senyap */ }
}

// Butang wishlist global pada kad listing
document.addEventListener('click', async (e) => {
  const b = e.target.closest('[data-wish]');
  if (!b) return;
  e.preventDefault();
  if (!session.me) { navigate(`/login?next=${encodeURIComponent(parseHash().path)}`); return; }
  const id = b.dataset.wish;
  const on = b.getAttribute('aria-pressed') === 'true';
  try {
    await (on ? (await import('./core/api.js')).del(`/api/wishlist/${id}`) : post(`/api/wishlist/${id}`));
    b.setAttribute('aria-pressed', String(!on));
    b.setAttribute('aria-label', on ? t('wishlist.add') : t('wishlist.remove'));
    toast(on ? t('wishlist.removed') : t('wishlist.saved'));
    window.dispatchEvent(new CustomEvent('wishlist-changed'));
  } catch (err) { showError(err); }
});

export async function logout() {
  await signOut();
  toast(t('auth.logged_out'));
  navigate('/');
}

async function boot() {
  installControls();
  mount($('#main'), loading());
  try {
    const cfg = await loadConfig();
    const s = cfg.settings;
    document.documentElement.dataset.today = cfg.today;
    // Warna jenama daripada tetapan; --sungai mod gelap kekal cerah supaya kontras terjaga.
    if (s.primary_color) document.documentElement.style.setProperty('--brand', s.primary_color);
    if (s.secondary_color) document.documentElement.style.setProperty('--brand-accent', s.secondary_color);
    await initAuth();
  } catch (e) {
    mount($('#main'), html`<div class="wrap empty"><h1>${t('page.server_unreachable')}</h1><p>${e.message}</p><button class="btn btn-primary" id="retry">${t('common.try_again')}</button></div>`);
    $('#retry').onclick = () => location.reload();
    return;
  }
  onSession(() => { renderShell(parseHash().path); pollUnread(); });
  window.addEventListener('hashchange', route);
  // Tukar bahasa: render semula rangka dan halaman semasa tanpa memuat semula pelayar.
  document.addEventListener('change', async (e) => {
    const sel = e.target.closest('[data-lang]');
    if (!sel) return;
    await changeLocale(sel.value);
  });
  window.addEventListener('locale-changed', () => route());
  const onScroll = () => $('#topbar').classList.toggle('scrolled', window.scrollY > 8);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
  window.addEventListener('resize', () => { const a = $('#topbar .acct'); if (a) a.style.display = window.innerWidth >= 900 ? 'none' : ''; });
  await route();
  pollUnread();
  setInterval(pollUnread, 30_000);
}

boot();
